import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles as SparklesIcon, X } from 'lucide-react';

/**
 * Global helper function to trigger the UFO Launch Animation
 * Call this ONLY AFTER form validation passes and form submission succeeds.
 * @param {HTMLElement | EventTarget | null} originElem - Optional submit button element to launch from
 */
export const triggerUFOAnimation = (originElem = null) => {
  let rect = null;
  let targetNode = originElem;

  // Extract element from React synthetic event or DOM event if passed
  if (originElem && originElem.nativeEvent) {
    targetNode =
      originElem.nativeEvent.submitter ||
      originElem.currentTarget?.querySelector?.('button[type="submit"]') ||
      originElem.currentTarget ||
      originElem.target;
  } else if (originElem && originElem.target && typeof originElem.target.getBoundingClientRect === 'function' && !originElem.getBoundingClientRect) {
    targetNode = originElem.target;
  }

  if (targetNode && typeof targetNode.getBoundingClientRect === 'function') {
    const r = targetNode.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) {
      rect = r;
    }
  }

  window.dispatchEvent(
    new CustomEvent('launch-ufo-animation', {
      detail: {
        originRect: rect
          ? {
              left: rect.left + rect.width / 2,
              top: rect.top + rect.height / 2,
            }
          : null,
      },
    })
  );
};

export default function SubmissionUFOAnimation() {
  const [animState, setAnimState] = useState('idle'); // 'idle' | 'popping' | 'flying_to_center' | 'hovering' | 'spinning' | 'ascending' | 'done'
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [showPopup, setShowPopup] = useState(false);
  const [burstSparkles, setBurstSparkles] = useState([]);

  useEffect(() => {
    const handleLaunch = (e) => {
      const rect = e.detail?.originRect;
      const startX = rect ? rect.left : window.innerWidth / 2;
      const startY = rect ? rect.top : window.innerHeight - 100;

      setStartPos({ x: startX, y: startY });
      setShowPopup(false);

      // 1. Generate radial sparkle burst right at the button location (24 particles)
      const newBurst = Array.from({ length: 24 }).map((_, i) => {
        const angle = (i / 24) * Math.PI * 2 + (Math.random() * 0.2 - 0.1);
        const dist = 50 + Math.random() * 70;
        return {
          id: i,
          targetX: Math.cos(angle) * dist,
          targetY: Math.sin(angle) * dist,
          color: i % 3 === 0 ? '#F3453F' : i % 3 === 1 ? '#FFFFFF' : '#FFD700',
          size: 14 + Math.random() * 12,
          delay: Math.random() * 0.12,
        };
      });
      setBurstSparkles(newBurst);

      setAnimState('popping');

      // Timeline sequence:
      // 0.0s - 0.7s: Pop animation & button burst sparkles
      // 0.7s - 2.2s: Fly from CTA button to viewport center
      // 2.2s - 5.2s: Hover/float gently for exactly 3 seconds
      // 5.2s - 8.2s: Realistic 3D Saucer Spin for exactly 3 seconds
      // 8.2s - 10.0s: Fly upward off screen & show success popup

      const timer1 = setTimeout(() => {
        setAnimState('flying_to_center');
      }, 700);

      const timer2 = setTimeout(() => {
        setAnimState('hovering');
      }, 2200);

      const timer3 = setTimeout(() => {
        setAnimState('spinning');
      }, 5200);

      const timer4 = setTimeout(() => {
        setAnimState('ascending');
        setShowPopup(true);
      }, 8200);

      const timer5 = setTimeout(() => {
        setAnimState('done');
        setBurstSparkles([]);
      }, 10000);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        clearTimeout(timer4);
        clearTimeout(timer5);
      };
    };

    window.addEventListener('launch-ufo-animation', handleLaunch);
    return () => window.removeEventListener('launch-ufo-animation', handleLaunch);
  }, []);

  if (animState === 'idle' && !showPopup && burstSparkles.length === 0) return null;

  const targetX = typeof window !== 'undefined' ? window.innerWidth / 2 : 500;
  const targetY = typeof window !== 'undefined' ? window.innerHeight * 0.48 : 350;

  // Determine current position / animation variant for UFO
  let ufoAnimate = {};
  let ufoTransition = {};

  if (animState === 'popping') {
    ufoAnimate = {
      x: startPos.x - 72,
      y: startPos.y - 47,
      scale: [0, 1.2, 1],
      opacity: 1,
      rotateX: 0,
      rotateZ: 0,
      rotateY: 0,
    };
    ufoTransition = { duration: 0.7, ease: [0.175, 0.885, 0.32, 1.275] };
  } else if (animState === 'flying_to_center') {
    ufoAnimate = {
      x: targetX - 72,
      y: targetY - 47,
      scale: 1,
      opacity: 1,
      rotateX: 0,
      rotateZ: [-6, 0],
      rotateY: 0,
    };
    ufoTransition = { duration: 1.5, ease: [0.25, 0.1, 0.25, 1] };
  } else if (animState === 'hovering') {
    ufoAnimate = {
      x: targetX - 72,
      y: [targetY - 47, targetY - 58, targetY - 47, targetY - 58, targetY - 47],
      scale: 1,
      opacity: 1,
      rotateX: 0,
      rotateZ: [-3, 3, -3],
      rotateY: 0,
    };
    ufoTransition = { duration: 3.0, ease: 'easeInOut' };
  } else if (animState === 'spinning') {
    // True 3D horizontal rotation around its vertical Y-axis using rotateY(360deg)
    ufoAnimate = {
      x: targetX - 72,
      y: targetY - 47,
      scale: 1,
      opacity: 1,
      rotateX: 0,
      rotateZ: 0,
      rotateY: [0, 360, 720, 1080],
    };
    ufoTransition = { duration: 3.0, ease: 'easeInOut' };
  } else if (animState === 'ascending') {
    ufoAnimate = {
      x: targetX - 72,
      y: -220,
      scale: 0.15,
      opacity: [1, 0.8, 0],
      rotateX: 0,
      rotateZ: 0,
      rotateY: 0,
    };
    ufoTransition = { duration: 1.8, ease: 'easeIn' };
  }

  // Sparkle positions around UFO body (sized for 144x93 box)
  const sparkles = [
    { id: 1, color: '#F3453F', size: 18, x: -35, y: -25, delay: 0.0, scale: [0, 1.3, 0] },
    { id: 2, color: '#FFFFFF', size: 16, x: 130, y: -15, delay: 0.15, scale: [0, 1.2, 0] },
    { id: 3, color: '#F3453F', size: 22, x: 135, y: 35, delay: 0.3, scale: [0, 1.4, 0] },
    { id: 4, color: '#FFFFFF', size: 18, x: -40, y: 40, delay: 0.45, scale: [0, 1.2, 0] },
    { id: 5, color: '#F3453F', size: 20, x: 45, y: -40, delay: 0.2, scale: [0, 1.5, 0] },
  ];

  return (
    <>
      {/* Overlay Layer for Button Sparkle Burst & UFO */}
      <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden" style={{ perspective: 1200 }}>
        {/* BUTTON RADIAL SPARKLE BURST */}
        {burstSparkles.map((sp) => (
          <motion.div
            key={sp.id}
            initial={{
              x: startPos.x - sp.size / 2,
              y: startPos.y - sp.size / 2,
              scale: 0,
              opacity: 1,
            }}
            animate={{
              x: startPos.x + sp.targetX - sp.size / 2,
              y: startPos.y + sp.targetY - sp.size / 2,
              scale: [0, 1.8, 1.2, 0],
              opacity: [1, 1, 0.8, 0],
            }}
            transition={{
              duration: 0.9,
              delay: sp.delay,
              ease: [0.16, 1, 0.3, 1],
            }}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
            }}
          >
            <svg width={sp.size} height={sp.size} viewBox="0 0 24 24">
              <path
                d="M12 0 C12 6.6, 17.4 12, 24 12 C17.4 12, 12 17.4, 12 24 C12 17.4, 6.6 12, 0 12 C6.6 12, 12 6.6, 12 0 Z"
                fill={sp.color}
              />
            </svg>
          </motion.div>
        ))}

        {/* UFO BODY */}
        {animState !== 'idle' && animState !== 'done' && (
          <motion.div
            initial={{ x: startPos.x - 72, y: startPos.y - 47, scale: 0, opacity: 0 }}
            animate={ufoAnimate}
            transition={ufoTransition}
            style={{ transformStyle: 'preserve-3d' }}
            className="absolute left-0 top-0 w-[144px] h-[93px] flex items-center justify-center"
          >
            {/* Sparkles around UFO */}
            {(animState === 'popping' || animState === 'flying_to_center' || animState === 'ascending') &&
              sparkles.map((sp) => (
                <motion.div
                  key={sp.id}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{
                    opacity: [0, 1, 0],
                    scale: sp.scale,
                    y: [sp.y, sp.y - 12],
                  }}
                  transition={{
                    duration: 0.9,
                    delay: sp.delay,
                    repeat: animState === 'ascending' ? Infinity : 0,
                  }}
                  style={{
                    position: 'absolute',
                    left: `${72 + sp.x}px`,
                    top: `${47 + sp.y}px`,
                  }}
                >
                  <svg width={sp.size} height={sp.size} viewBox="0 0 24 24">
                    <path
                      d="M12 0 C12 6.6, 17.4 12, 24 12 C17.4 12, 12 17.4, 12 24 C12 17.4, 6.6 12, 0 12 C6.6 12, 12 6.6, 12 0 Z"
                      fill={sp.color}
                    />
                  </svg>
                </motion.div>
              ))}

            {/* Futuristic 3D Flat UFO Body */}
            <div className="relative w-full h-full flex items-center justify-center">
              <svg width="144" height="93" viewBox="0 0 140 90">
                <defs>
                  {/* UFO Body Gradient */}
                  <linearGradient id="ufoBodyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#181F32" />
                    <stop offset="50%" stopColor="#181F32" />
                    <stop offset="100%" stopColor="#0F1420" />
                  </linearGradient>

                  {/* UFO Bottom Hull Gradient */}
                  <linearGradient id="ufoBottomGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#181F32" />
                    <stop offset="100%" stopColor="#181F32" />
                  </linearGradient>

                  {/* Glass Dome Radial Gradient */}
                  <radialGradient id="ufoDomeGrad" cx="35%" cy="30%" r="70%">
                    <stop offset="0%" stopColor="rgba(255, 255, 255, 0.95)" />
                    <stop offset="40%" stopColor="rgba(200, 220, 245, 0.65)" />
                    <stop offset="85%" stopColor="rgba(24, 31, 50, 0.85)" />
                    <stop offset="100%" stopColor="rgba(24, 31, 50, 0.95)" />
                  </radialGradient>

                  {/* Thruster Laser Beam Gradient */}
                  <linearGradient id="ufoBeamGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="rgba(243, 69, 63, 0.85)" />
                    <stop offset="40%" stopColor="rgba(255, 255, 255, 0.6)" />
                    <stop offset="100%" stopColor="rgba(243, 69, 63, 0)" />
                  </linearGradient>
                </defs>

                {/* 1. Downward Tractor Beam / Thruster Light */}
                <polygon points="45,52 95,52 115,88 25,88" fill="url(#ufoBeamGrad)" opacity="0.85" />

                {/* 2. Saucer Lower Rim */}
                <path
                  d="M 12 45 C 12 63, 128 63, 128 45 C 128 53, 12 53, 12 45 Z"
                  fill="url(#ufoBottomGrad)"
                  stroke="#F3453F"
                  strokeWidth="1"
                />

                {/* 3. Saucer Main Metallic Body */}
                <path
                  d="M 8 42 C 8 24, 132 24, 132 42 C 132 58, 8 58, 8 42 Z"
                  fill="url(#ufoBodyGrad)"
                  stroke="rgba(255, 255, 255, 0.5)"
                  strokeWidth="1.2"
                />

                {/* 4. Rim LED Lights */}
                <circle cx="22" cy="44" r="3.2" fill="#F3453F" />
                <circle cx="44" cy="48" r="3.8" fill="#FFFFFF" />
                <circle cx="70" cy="49" r="4.2" fill="#F3453F" />
                <circle cx="96" cy="48" r="3.8" fill="#FFFFFF" />
                <circle cx="118" cy="44" r="3.2" fill="#F3453F" />

                {/* 5. 3D Glass Cockpit Dome */}
                <ellipse
                  cx="70"
                  cy="28"
                  rx="28"
                  ry="18"
                  fill="url(#ufoDomeGrad)"
                  stroke="rgba(255, 255, 255, 0.85)"
                  strokeWidth="1.5"
                />

                {/* 6. Specular Curved Glass Reflection */}
                <ellipse cx="62" cy="22" rx="10" ry="4" fill="#FFFFFF" opacity="0.85" transform="rotate(-15 62 22)" />
                <path d="M 52 23 C 62 17, 78 17, 88 23 C 78 20, 62 20, 52 23 Z" fill="#FFFFFF" opacity="0.9" />
              </svg>
            </div>
          </motion.div>
        )}
      </div>

      {/* SUCCESS POPUP */}
      <AnimatePresence>
        {showPopup && (
          <div className="fixed inset-0 pointer-events-auto z-[10000] flex items-center justify-center p-4 bg-black/60">
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -10 }}
              transition={{ duration: 0.4, cubicBezier: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-md p-6 sm:p-8 rounded-2xl text-center overflow-hidden font-sans border border-white/20 bg-[#181F32]"
            >
              {/* Success Icon */}
              <div className="mx-auto mb-4 w-14 h-14 rounded-2xl flex items-center justify-center bg-[#202942] border border-white/20 text-white relative z-10">
                <Check size={28} className="text-[#F3453F]" />
              </div>

              {/* Title */}
              <h3 className="text-xl sm:text-2xl font-extrabold text-white font-heading mb-2 relative z-10 tracking-tight">
                Your request has been <span className="text-[#F3453F]">deployed</span>.
              </h3>

              {/* Supporting Text */}
              <p className="text-white/95 text-sm sm:text-base font-semibold font-sans mb-6 relative z-10">
                We will contact you ASAP.
              </p>

              {/* Action Button */}
              <button
                type="button"
                onClick={() => setShowPopup(false)}
                className="w-full btn-glass btn-glass-submit py-3.5 text-sm sm:text-base font-bold relative z-10 transition-all duration-300"
              >
                Close & Return
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
